import React, { useState } from 'react';
import { 
  Sliders, Plus, Trash2, Check, RotateCcw, Info, 
  ToggleLeft, ToggleRight, Layout, Palette, ChevronDown, ChevronUp 
} from 'lucide-react';
import { ReportTemplate, ReportSectionConfig } from '../types';

interface TemplateManagerProps {
  templates: ReportTemplate[];
  onChangeTemplates: (newTemplates: ReportTemplate[]) => void;
  onReset: () => void;
  language: 'uk' | 'en' | 'pl';
}

const COLOR_ACCENTS = [
  { id: 'cyan', name: 'Cyan Tech', colorClass: 'bg-cyan-500 border-cyan-400 text-cyan-400' },
  { id: 'emerald', name: 'Emerald Eco', colorClass: 'bg-emerald-500 border-emerald-400 text-emerald-400' },
  { id: 'purple', name: 'Purple Neon', colorClass: 'bg-purple-500 border-purple-400 text-purple-400' },
  { id: 'amber', name: 'Amber Gold', colorClass: 'bg-amber-500 border-amber-400 text-amber-400' },
  { id: 'rose', name: 'Rose Red', colorClass: 'bg-rose-500 border-rose-400 text-rose-400' }
] as const;

export default function TemplateManager({ templates, onChangeTemplates, onReset, language }: TemplateManagerProps) {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(templates[0]?.id || 'scientific');
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [newSectionDesc, setNewSectionDesc] = useState('');
  const [isAddingSection, setIsAddingSection] = useState(false);
  const [editingTemplateName, setEditingTemplateName] = useState<string | null>(null);
  const [tempNameInput, setTempNameInput] = useState('');

  const activeTemplate = templates.find(t => t.id === selectedTemplateId) || templates[0];

  const handleToggleSection = (sectionId: string) => {
    const updated = templates.map(t => {
      if (t.id === activeTemplate.id) {
        return {
          ...t,
          sections: t.sections.map(s => s.id === sectionId ? { ...s, enabled: !s.enabled } : s)
        };
      }
      return t;
    });
    onChangeTemplates(updated);
  };

  const handleAddSection = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSectionTitle.trim() || !newSectionDesc.trim()) return;

    const newSec: ReportSectionConfig = {
      id: 'custom_' + Date.now(),
      title: newSectionTitle.trim(),
      description: newSectionDesc.trim(),
      enabled: true
    };

    const updated = templates.map(t => {
      if (t.id === activeTemplate.id) {
        return {
          ...t,
          sections: [...t.sections, newSec]
        };
      }
      return t;
    });

    onChangeTemplates(updated);
    setNewSectionTitle('');
    setNewSectionDesc('');
    setIsAddingSection(false);
  };

  const handleDeleteSection = (sectionId: string) => {
    const updated = templates.map(t => {
      if (t.id === activeTemplate.id) {
        return {
          ...t,
          sections: t.sections.filter(s => s.id !== sectionId)
        };
      }
      return t;
    });
    onChangeTemplates(updated);
  };

  const handleChangeAccent = (accent: typeof COLOR_ACCENTS[number]['id']) => {
    const updated = templates.map(t => {
      if (t.id === activeTemplate.id) {
        return { ...t, styleAccent: accent };
      }
      return t;
    });
    onChangeTemplates(updated);
  };

  const handleDuplicateTemplate = () => {
    const newId = 'duplicate_' + Date.now();
    const newTmpl: ReportTemplate = {
      ...activeTemplate,
      id: newId,
      name: `${activeTemplate.name} (Копія)`,
      isCustom: true,
      sections: activeTemplate.sections.map(s => ({ ...s }))
    };
    onChangeTemplates([...templates, newTmpl]);
    setSelectedTemplateId(newId);
  };

  const handleDeleteTemplate = () => {
    if (templates.length <= 1) return;
    const filtered = templates.filter(t => t.id !== activeTemplate.id);
    onChangeTemplates(filtered);
    setSelectedTemplateId(filtered[0].id);
  };

  const handleSaveName = () => {
    if (!tempNameInput.trim()) return;
    const updated = templates.map(t => {
      if (t.id === activeTemplate.id) {
        return { ...t, name: tempNameInput.trim() };
      }
      return t;
    });
    onChangeTemplates(updated);
    setEditingTemplateName(null);
  };

  if (!activeTemplate) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 relative shadow-xs">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-4 mb-5">
        <div>
          <h2 className="text-xs font-bold font-mono text-slate-800 flex items-center gap-2 uppercase tracking-wider">
            <Sliders className="w-4 h-4 text-red-500" />
            {language === 'uk' ? 'КОНСТРУКТОР ШАБЛОНІВ ЗВІТІВ' : 'REPORT TEMPLATE CUSTOMIZER'}
          </h2>
          <p className="text-[10px] text-slate-400 font-mono mt-1">
            {language === 'uk' 
              ? 'Налаштуйте структуру розділів, фокус ШІ та колірну палітру для автоматичних звітів.' 
              : 'Customize document sections, AI focus prompts, and theme highlights for report synthesis.'}
          </p>
        </div>
        <button
          onClick={onReset}
          className="px-3 py-1 bg-slate-100 border border-slate-200 text-slate-600 hover:text-slate-800 rounded text-[10px] font-mono font-bold uppercase tracking-wider flex items-center gap-1.5 cursor-pointer transition-colors"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          {language === 'uk' ? 'СКИДАТИ' : 'RESET'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Templates list on the left */}
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 font-mono">
              {language === 'uk' ? 'ВИБЕРІТЬ ШАБЛОН ДЛЯ НАЛАШТУВАННЯ:' : 'SELECT TEMPLATE TO CONFIGURE:'}
            </label>
            <div className="space-y-2">
              {templates.map(t => (
                <button
                  key={t.id}
                  onClick={() => {
                    setSelectedTemplateId(t.id);
                    setEditingTemplateName(null);
                  }}
                  className={`w-full text-left p-3 rounded border text-xs font-mono transition-all duration-150 flex items-start justify-between cursor-pointer ${
                    t.id === activeTemplate.id
                      ? 'bg-red-50/50 border-red-500/50 text-slate-800 font-bold'
                      : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-800'
                  }`}
                >
                  <div className="min-w-0 pr-2">
                    <p className="font-bold uppercase tracking-tight truncate">{t.name}</p>
                    <p className="text-[9px] text-slate-400 mt-0.5 line-clamp-1">{t.description}</p>
                  </div>
                  <span className={`w-2 h-2 rounded-full self-center shrink-0 ${
                    t.styleAccent === 'cyan' ? 'bg-cyan-500' :
                    t.styleAccent === 'emerald' ? 'bg-emerald-500' :
                    t.styleAccent === 'purple' ? 'bg-purple-500' :
                    t.styleAccent === 'amber' ? 'bg-amber-500' : 'bg-rose-500'
                  }`} />
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleDuplicateTemplate}
              className="flex-1 py-2 bg-slate-100 border border-slate-200 hover:bg-slate-200/50 text-slate-600 hover:text-slate-800 rounded text-[10px] font-mono font-bold uppercase tracking-wider flex items-center justify-center gap-1 cursor-pointer transition-colors"
            >
              <Plus className="w-3.5 h-3.5 text-red-500" />
              {language === 'uk' ? 'ДУБЛЮВАТИ' : 'DUPLICATE'}
            </button>
            {activeTemplate.isCustom && (
              <button
                onClick={handleDeleteTemplate}
                className="py-2 px-3 bg-red-50 border border-red-200 hover:bg-red-100 text-red-600 hover:text-red-700 rounded text-[10px] font-mono font-bold uppercase flex items-center justify-center cursor-pointer transition-colors"
                title={language === 'uk' ? 'Видалити шаблон' : 'Delete template'}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Section layout & editing on the right */}
        <div className="lg:col-span-2 space-y-5">
          {/* Active template header */}
          <div className="bg-slate-50 p-4 rounded border border-slate-200 space-y-3">
            <div className="flex items-center justify-between gap-4">
              {editingTemplateName !== null ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="text"
                    value={tempNameInput}
                    onChange={(e) => setTempNameInput(e.target.value)}
                    className="flex-1 bg-white text-slate-800 text-xs px-2 py-1 rounded border border-red-500/50 font-mono focus:outline-none"
                    placeholder="Назва шаблону"
                  />
                  <button
                    onClick={handleSaveName}
                    className="p-1 bg-red-500 hover:bg-red-600 text-white rounded cursor-pointer"
                  >
                    <Check className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-bold text-slate-800 font-mono uppercase tracking-wide">
                    {activeTemplate.name}
                  </h3>
                  <button
                    onClick={() => {
                      setEditingTemplateName(activeTemplate.id);
                      setTempNameInput(activeTemplate.name);
                    }}
                    className="text-[9px] font-mono text-red-500 hover:underline cursor-pointer"
                  >
                    [{language === 'uk' ? 'ЗМІНИТИ НАЗВУ' : 'RENAME'}]
                  </button>
                </div>
              )}

              {/* Palette Accent picker */}
              <div className="flex items-center gap-1 bg-slate-100 p-1 rounded border border-slate-200">
                <Palette className="w-3 h-3 text-slate-400 mr-1" />
                {COLOR_ACCENTS.map(acc => (
                  <button
                    key={acc.id}
                    onClick={() => handleChangeAccent(acc.id)}
                    className={`w-4 h-4 rounded-full border transition-all cursor-pointer ${
                      activeTemplate.styleAccent === acc.id
                        ? 'border-white scale-110 shadow-md'
                        : 'border-transparent opacity-60 hover:opacity-100'
                    } ${
                      acc.id === 'cyan' ? 'bg-cyan-500' :
                      acc.id === 'emerald' ? 'bg-emerald-500' :
                      acc.id === 'purple' ? 'bg-purple-500' :
                      acc.id === 'amber' ? 'bg-amber-500' : 'bg-rose-500'
                    }`}
                    title={acc.name}
                  />
                ))}
              </div>
            </div>
            <p className="text-[10px] text-slate-500 leading-relaxed font-mono">
              {activeTemplate.description}
            </p>
          </div>

          {/* Section configs list */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest font-mono">
                {language === 'uk' ? 'СТРУКТУРА ТА ВМІСТ РОЗДІЛІВ:' : 'SECTION HIERARCHY & PROMPTS:'}
              </label>
              <button
                onClick={() => setIsAddingSection(!isAddingSection)}
                className="text-[9px] font-mono text-red-500 hover:text-red-600 flex items-center gap-1 cursor-pointer"
              >
                <Plus className="w-3 h-3" />
                <span>{language === 'uk' ? 'ДОДАТИ РОЗДІЛ' : 'ADD SECTION'}</span>
              </button>
            </div>

            {/* Adding Section Form */}
            {isAddingSection && (
              <form onSubmit={handleAddSection} className="p-4 bg-red-50/50 rounded border border-red-200 space-y-3">
                <p className="text-[9px] font-bold text-red-600 font-mono uppercase tracking-wider">
                  {language === 'uk' ? 'НОВИЙ РОЗДІЛ ЗВІТУ' : 'NEW DOCUMENT SECTION'}
                </p>
                <div className="space-y-2">
                  <input
                    type="text"
                    required
                    value={newSectionTitle}
                    onChange={(e) => setNewSectionTitle(e.target.value)}
                    placeholder={language === 'uk' ? "Наприклад: 4. Вплив на екологію" : "E.g., 4. Environmental Footprint"}
                    className="w-full text-[10px] font-bold bg-white text-slate-800 px-2.5 py-1.5 rounded border border-slate-200 font-mono focus:outline-none focus:border-red-500"
                  />
                  <textarea
                    required
                    value={newSectionDesc}
                    onChange={(e) => setNewSectionDesc(e.target.value)}
                    placeholder={language === 'uk' ? "Опишіть ШІ, про що писати в цьому розділі, які дані зіставити..." : "Give AI directions for compiling this section: which metrics to cross-reference..."}
                    rows={2}
                    className="w-full text-[10px] bg-white text-slate-800 px-2.5 py-1.5 rounded border border-slate-200 font-mono focus:outline-none focus:border-red-500 resize-none"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setIsAddingSection(false)}
                    className="px-2.5 py-1 text-[9px] font-mono uppercase text-slate-500 hover:text-slate-800 cursor-pointer"
                  >
                    {language === 'uk' ? 'Скасувати' : 'Cancel'}
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-[9px] font-bold font-mono uppercase rounded cursor-pointer"
                  >
                    {language === 'uk' ? 'Додати' : 'Create'}
                  </button>
                </div>
              </form>
            )}

            {/* Sections cards list */}
            <div className="space-y-2.5">
              {activeTemplate.sections.map((sec, idx) => (
                <div 
                  key={sec.id}
                  className={`p-3.5 rounded border transition-colors flex items-start gap-3.5 ${
                    sec.enabled 
                      ? 'bg-slate-50 border-slate-200 text-slate-700' 
                      : 'bg-slate-50/30 border-slate-200/50 opacity-55 text-slate-400'
                  }`}
                >
                  {/* Number bubble */}
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center font-mono text-[10px] font-bold ${
                    sec.enabled 
                      ? 'bg-white text-red-500 border border-slate-200' 
                      : 'bg-slate-100 text-slate-400 border border-transparent'
                  }`}>
                    {idx + 1}
                  </div>

                  {/* Body */}
                  <div className="flex-1 min-w-0 space-y-1">
                    <p className={`text-[11px] font-mono font-bold leading-none ${sec.enabled ? 'text-slate-800 font-bold' : 'text-slate-400'}`}>
                      {sec.title}
                    </p>
                    <p className="text-[10px] font-mono text-slate-500 leading-relaxed">
                      {sec.description}
                    </p>
                  </div>

                  {/* Toggle & delete */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => handleToggleSection(sec.id)}
                      className="text-slate-400 hover:text-white cursor-pointer"
                      title={sec.enabled ? (language === 'uk' ? 'Вимкнути' : 'Disable') : (language === 'uk' ? 'Увімкнути' : 'Enable')}
                    >
                      {sec.enabled ? (
                        <ToggleRight className="w-5 h-5 text-red-500" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-slate-400" />
                      )}
                    </button>
                    {activeTemplate.sections.length > 1 && (
                      <button
                        onClick={() => handleDeleteSection(sec.id)}
                        className="p-1 hover:bg-slate-100 text-slate-400 hover:text-red-500 rounded cursor-pointer transition-colors"
                        title={language === 'uk' ? 'Видалити розділ' : 'Delete section'}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
